/**
 * Mounted around `<KellyDock />` in `account-root/view.jsx`, the only position that works: the dock is a SIBLING
 * of the account `Outlet` there, so `DashboardAnalyticsProvider` never nests inside this one, and `KellyDock`'s
 * own body is still covered — a provider placed among the dock's children would miss it, for the same reason
 * `KellyChatProvider`'s body is out of reach of anything below it.
 */

import type { ReactNode } from "react"
import { useMemo } from "react"
import AnalyticsContext, { useAnalyticsWithProperties } from "connected-components/analytics/context"
import { useKellyChatOptional } from "../chatbox/kelly-chat-context"
import { useKellyPrefixContribution } from "../chatbox/use-kelly-analytics"
import { useKellySurfaceProperties } from "./surface-context"

export function KellyDockAnalyticsProvider({ children }: { children: ReactNode }) {
  // Read rather than recompute: `use-analytics-properties.ts` stays the single owner of a surface fact, and
  // the gate's provider has already evaluated it this render.
  const surfaceProperties = useKellySurfaceProperties()
  // Null while the gate's boundary renders the app bare, or off an app section.
  const activeThreadId = useKellyChatOptional()?.activeThreadId

  const contextProperties = useMemo(
    () => (activeThreadId != null ? { ...surfaceProperties, threadId: activeThreadId } : surfaceProperties),
    [surfaceProperties, activeThreadId],
  )

  const contextPrefix = useKellyPrefixContribution()

  const value = useAnalyticsWithProperties(contextProperties, contextPrefix)

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  )
}
